import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../constants/zIndex';

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  zIndex?: number;
  disableBackdropClick?: boolean;
}

let openCount = 0;

function updateBodyLock() {
  document.body.style.overflow = openCount > 0 ? 'hidden' : '';
}

export default function ModalShell({
  isOpen,
  onClose,
  children,
  zIndex = Z.MODAL_BACKDROP,
  disableBackdropClick = false,
}: ModalShellProps) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      openCount += 1;
      updateBodyLock();
      requestAnimationFrame(() => setEntered(true));
    }
    return () => {
      if (openCount > 0) openCount -= 1;
      updateBodyLock();
      setEntered(false);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const contentZ = zIndex + 1;

  const shell = (
    <div
      className="fixed inset-0"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
    >
      <div
        className="absolute inset-0 bg-black/50"
        style={{ opacity: entered ? 1 : 0, transition: 'opacity 200ms ease-out' }}
        onClick={disableBackdropClick ? undefined : onClose}
      />
      <div
        className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none"
        style={{ zIndex: contentZ }}
      >
        <div
          className="pointer-events-auto max-h-[90vh] overflow-y-auto"
          style={{
            opacity: entered ? 1 : 0,
            transform: entered ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)',
            transition: 'opacity 200ms ease-out, transform 200ms ease-out',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(shell, document.body);
}
